'use client';

import { useEffect, useRef, useState } from 'react';
import { ContactInfo, EmailPrefix } from '../lib/types';

interface Step7Props {
  domain: string;
  selectedEmails: EmailPrefix[];
  contact: ContactInfo;
  onPaymentSuccess: () => void;
  onCallbackRequest: () => void;
  onBack: () => void;
}

declare global {
  interface Window {
    FedaPay?: {
      init: (config: Record<string, unknown>) => { open: () => void };
      CHECKOUT_COMPLETED?: string;
      DIALOG_DISMISSED?: string;
    };
  }
}

const PRICE = Number(process.env.NEXT_PUBLIC_PRICE || 25000);
const PUBLIC_KEY = process.env.NEXT_PUBLIC_FEDAPAY_PUBLIC_KEY || '';

export default function Step7Payment({
  domain,
  selectedEmails,
  contact,
  onPaymentSuccess,
  onCallbackRequest,
  onBack,
}: Step7Props) {
  const [choice, setChoice] = useState<'pay' | 'callback' | null>(null);
  const [loading, setLoading] = useState(false);
  const [callbackSent, setCallbackSent] = useState(false);
  const scriptLoaded = useRef(false);

  useEffect(() => {
    if (scriptLoaded.current) return;
    const existing = document.querySelector('script[src*="fedapay"]');
    if (existing) {
      scriptLoaded.current = true;
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://cdn.fedapay.com/checkout.js?v=1.1.7';
    script.async = true;
    script.onload = () => {
      scriptLoaded.current = true;
    };
    document.head.appendChild(script);
  }, []);

  async function handlePay() {
    setLoading(true);

    try {
      if (!window.FedaPay) {
        alert('Le module de paiement est en cours de chargement. Réessayez dans quelques secondes.');
        setLoading(false);
        return;
      }

      const widget = window.FedaPay.init({
        public_key: PUBLIC_KEY,
        transaction: {
          amount: PRICE,
          description: `Email Pro: ${domain} (${selectedEmails.length} adresses)`,
        },
        customer: {
          email: contact.email,
          lastname: contact.fullName.split(' ').slice(-1)[0] || contact.fullName,
          firstname: contact.fullName.split(' ').slice(0, -1).join(' ') || contact.fullName,
          phone_number: {
            number: contact.phone.replace(/\D/g, '').slice(-8),
            country: 'BJ',
          },
        },
        onComplete: async (reason: string) => {
          if (reason === window.FedaPay?.CHECKOUT_COMPLETED) {
            // Notify admin
            await fetch('/api/notify-admin', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                type: 'payment',
                domain,
                emails: selectedEmails,
                contact,
              }),
            });
            onPaymentSuccess();
          }
          setLoading(false);
        },
      });

      widget.open();
    } catch {
      alert('Erreur lors de l\'initialisation du paiement. Veuillez réessayer.');
      setLoading(false);
    }
  }

  async function handleCallback() {
    setLoading(true);
    try {
      await fetch('/api/notify-admin', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'callback',
          domain,
          emails: selectedEmails,
          contact,
        }),
      });
      setCallbackSent(true);
      setTimeout(() => onCallbackRequest(), 1500);
    } catch {
      alert('Erreur. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="animate-fade-in max-w-lg mx-auto">
      <h2 className="text-2xl font-bold mb-2">Finalisez votre commande</h2>
      <p className="text-muted mb-8">Choisissez comment vous souhaitez procéder.</p>

      <div className="space-y-3">
        {/* Option: Pay now */}
        <button
          onClick={() => setChoice('pay')}
          className={`w-full p-5 rounded-xl border-2 text-left transition-all duration-200 cursor-pointer ${
            choice === 'pay'
              ? 'border-primary bg-primary-light/50'
              : 'border-border hover:border-primary/30'
          }`}
        >
          <div className="flex items-start gap-4">
            <div
              className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 mt-0.5 ${
                choice === 'pay' ? 'border-primary bg-primary' : 'border-border'
              }`}
            >
              {choice === 'pay' && <div className="w-2 h-2 bg-white rounded-full" />}
            </div>
            <div>
              <p className="font-semibold mb-1">Activer maintenant</p>
              <p className="text-sm text-muted">
                Paiement sécurisé via Mobile Money. Vos emails seront configurés dans les 24h.
              </p>
              <p className="text-lg font-bold text-primary mt-2">
                {PRICE.toLocaleString('fr-FR')} XOF
              </p>
            </div>
          </div>
        </button>

        {/* Option: Callback */}
        <button
          onClick={() => setChoice('callback')}
          className={`w-full p-5 rounded-xl border-2 text-left transition-all duration-200 cursor-pointer ${
            choice === 'callback'
              ? 'border-primary bg-primary-light/50'
              : 'border-border hover:border-primary/30'
          }`}
        >
          <div className="flex items-start gap-4">
            <div
              className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 mt-0.5 ${
                choice === 'callback' ? 'border-primary bg-primary' : 'border-border'
              }`}
            >
              {choice === 'callback' && <div className="w-2 h-2 bg-white rounded-full" />}
            </div>
            <div>
              <p className="font-semibold mb-1">Être contacté par un conseiller</p>
              <p className="text-sm text-muted">
                Un conseiller vous rappellera pour finaliser la mise en place de vos emails.
              </p>
            </div>
          </div>
        </button>
      </div>

      {callbackSent && (
        <div className="mt-4 bg-green-50 text-success text-sm p-3 rounded-lg text-center font-medium animate-fade-in">
          Demande envoyée ! Un conseiller vous contactera très bientôt.
        </div>
      )}

      <div className="flex gap-3 mt-8">
        <button
          onClick={onBack}
          className="px-6 py-3 rounded-lg border border-border text-muted hover:bg-gray-50 transition-colors cursor-pointer"
        >
          Retour
        </button>
        <button
          onClick={choice === 'pay' ? handlePay : handleCallback}
          disabled={!choice || loading}
          className="flex-1 bg-primary hover:bg-primary-hover disabled:opacity-40 disabled:cursor-not-allowed text-white font-semibold py-3 rounded-lg transition-colors cursor-pointer"
        >
          {loading ? (
            <span className="flex items-center justify-center gap-2">
              <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Chargement...
            </span>
          ) : choice === 'pay' ? (
            'Payer maintenant'
          ) : choice === 'callback' ? (
            'Envoyer ma demande'
          ) : (
            'Choisissez une option'
          )}
        </button>
      </div>
    </div>
  );
}